// Visual flow graph for a registered flow. Drives off the same FlowSummary
// shape /api/settings/flows returns; we just turn `nodes[].exits` into edges
// and let dagre lay everything out top-to-bottom.
//
// Why a custom node type: the default ReactFlow node renders a plain box
// with just `data.label`. Each flow node carries an id, a goal sentence,
// max_turns, plus an entry tag — useful at a glance without a click.

import { useMemo } from "react";
import dagre from "@dagrejs/dagre";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

// Mirrors the FlowSummary shape from Settings.tsx — duplicated here so this
// component stays self-contained. Keep these two in sync if the bot adds
// fields to /list-flows.
type FlowNode = {
  id: string;
  goal: string;
  max_turns: number;
};

type FlowSpecLike = {
  id: string;
  nodes: Array<{
    id: string;
    goal: string;
    max_turns: number;
    exits: Array<[string, string]>;
  }>;
  entry_node: string;
};

type FlowNodeData = FlowNode & { is_entry: boolean; is_selected: boolean };

// Sized so 4-6 nodes fit on screen at typical settings-pane widths
// without forcing a zoom-out on first paint.
const NODE_WIDTH = 220;
const NODE_HEIGHT = 76;

function FlowNodeView({ data }: NodeProps<Node<FlowNodeData>>) {
  const cls = [
    "flow-node",
    data.is_entry ? "is-entry" : "",
    data.is_selected ? "is-selected" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div id={`flow-graph-node-${data.id}`} className={cls}>
      <Handle type="target" position={Position.Top} />
      <div className="flow-node-head">
        <code>{data.id}</code>
        {data.is_entry && <span className="flow-node-entry">entry</span>}
        <span className="flow-node-turns">max {data.max_turns}</span>
      </div>
      <div className="flow-node-body">{data.goal}</div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const NODE_TYPES = { flow: FlowNodeView };

/**
 * Run the spec's nodes + exits through dagre to get fixed positions.
 * Top-to-bottom layout — works well for retention flows that progress
 * linearly with branches off to handle_objection / transfer.
 */
function layout(spec: FlowSpecLike, selectedId: string | null): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 60 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of spec.nodes) {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  // Synthetic terminal node — many specs end with `→ end (always)` and we
  // want the arrow to point somewhere visible rather than dangling.
  const targets = new Set(spec.nodes.map((n) => n.id));
  let needsEnd = false;
  for (const n of spec.nodes) {
    for (const [target] of n.exits) {
      if (!targets.has(target)) {
        needsEnd = true;
      }
    }
  }
  if (needsEnd) {
    g.setNode("__end__", { width: 96, height: 36 });
  }

  for (const n of spec.nodes) {
    for (const [target] of n.exits) {
      const t = targets.has(target) ? target : "__end__";
      g.setEdge(n.id, t);
    }
  }
  dagre.layout(g);

  const nodes: Node[] = spec.nodes.map((n) => {
    const pos = g.node(n.id);
    return {
      id: n.id,
      type: "flow",
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      data: {
        id: n.id,
        goal: n.goal,
        max_turns: n.max_turns,
        is_entry: n.id === spec.entry_node,
        is_selected: n.id === selectedId,
      },
    };
  });
  if (needsEnd) {
    const pos = g.node("__end__");
    nodes.push({
      id: "__end__",
      type: "default",
      position: { x: pos.x - 48, y: pos.y - 18 },
      data: { label: "end" },
      style: {
        width: 96,
        padding: "0.4rem 0.6rem",
        fontSize: 12,
        textAlign: "center",
        background: "var(--surface-subtle)",
        border: "1px dashed var(--border)",
        borderRadius: "var(--r-md)",
        color: "var(--text-faint)",
      },
    });
  }

  // Edges. Multiple exits between the same pair are merged onto one edge
  // with both labels stacked — keeps the graph readable in the rare cases
  // where a node has two conditional paths to the same target.
  type EdgeKey = { source: string; target: string; conditions: string[] };
  const edgeMap = new Map<string, EdgeKey>();
  for (const n of spec.nodes) {
    for (const [target, condition] of n.exits) {
      const t = targets.has(target) ? target : "__end__";
      const key = `${n.id}::${t}`;
      const cur = edgeMap.get(key) ?? { source: n.id, target: t, conditions: [] };
      cur.conditions.push(condition);
      edgeMap.set(key, cur);
    }
  }
  const edges: Edge[] = [];
  for (const [key, ek] of edgeMap) {
    edges.push({
      id: key,
      source: ek.source,
      target: ek.target,
      label: ek.conditions.join(" / "),
      labelStyle: { fontSize: 10, fill: "var(--text-subtle)" },
      labelBgStyle: { fill: "var(--surface-base)" },
      labelBgPadding: [4, 2],
      style: { stroke: "var(--border-strong)" },
    });
  }
  return { nodes, edges };
}

export function FlowGraph({
  spec,
  selectedId = null,
  onSelectNode,
}: {
  spec: FlowSpecLike;
  /** Currently-selected node id. Highlighted via `is-selected` on the node card. */
  selectedId?: string | null;
  /** Fires when the user clicks a node. Pass null when the user clicks the
   *  empty pane (so callers can clear the inspector). */
  onSelectNode?: (id: string | null) => void;
}) {
  const { nodes, edges } = useMemo(() => layout(spec, selectedId), [spec, selectedId]);
  return (
    <div id={`flow-graph-${spec.id}`} className="flow-graph">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        // Read-only graph: nodes can be selected but not dragged/edited.
        nodesDraggable={false}
        nodesConnectable={false}
        edgesFocusable={false}
        onNodeClick={(_, node) => {
          // Synthetic terminal "__end__" node isn't part of spec.nodes —
          // ignore so the inspector doesn't try to look it up.
          if (node.id === "__end__") return;
          onSelectNode?.(node.id);
        }}
        onPaneClick={() => onSelectNode?.(null)}
      >
        <Background />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
}
